/**
 * Polling-day outbox: SOS alerts and turnout reports.
 *
 * The section / location outbox is the mobile LocalStore, reused unchanged; its row mapping only
 * knows those two kinds, so polling items live in their own tables in the SAME sql.js database and
 * have their own small sender. The rules are the mobile engine's rules:
 *
 *   2xx              → done; the result goes to polling_sent (shown as "sent ✓")
 *   no answer        → stays pending, exponential backoff, the run stops
 *   5xx / 429 / 408  → stays pending with backoff, later items still go
 *   401              → stop, keep everything, ask for login
 *   400 / 403 / 404  → failed with the server's message; retry or discard by hand
 *
 * SOS: every alert gets a random clientId once, when it is raised. Every retry sends the same id,
 * and the API never creates a second alert for it, so retries after a lost response are safe.
 * SOS items are sent before turnout items.
 *
 * Turnout: one queued report per booth + date + slot; entering it again before it was sent
 * replaces it (a report edited while its PUT is on the wire stays queued, like sections).
 */
import { ApiError, NetworkError } from "@core/api";
import { Emitter } from "@core/emitter";
import type { SqlDb } from "@core/sql";
import { backoffDelay, type SyncRunResult, type SyncTrigger, type Timers } from "@core/sync";
import type { OutboxError } from "@core/types";
import type { SosCategory, TurnoutSlot } from "@npac/rules";
import type { FieldApi, Sos, SosPhoto, Turnout } from "./api";

export interface SosPayload {
  category: SosCategory;
  note: string | null;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  /** device time the alert was raised */
  raisedAt: string;
  /** optional photo (already compressed); older queued items have none */
  photo?: SosPhoto | null;
}

export interface TurnoutPayload {
  slot: TurnoutSlot;
  /** polling date in India time, fixed when the report was entered (not when it is sent) */
  date: string;
  votesPolled: number;
  /** our party's votes so far, if the agent counted them */
  ourVotes?: number | null;
}

interface Base {
  id: number;
  assemblyId: string;
  boothCode: string;
  /** SOS: clientId. Turnout: "<date>|<slot>". */
  key: string;
  status: "pending" | "failed";
  attempts: number;
  nextAttemptAt: number;
  lastError: OutboxError | null;
  rev: number;
  createdAt: number;
  updatedAt: number;
}
export interface SosItem extends Base {
  kind: "sos";
  payload: SosPayload;
}
export interface TurnoutItem extends Base {
  kind: "turnout";
  payload: TurnoutPayload;
}
export type PollingItem = SosItem | TurnoutItem;

export interface SentRecord {
  id: number;
  kind: "sos" | "turnout";
  assemblyId: string;
  boothCode: string;
  key: string;
  payload: SosPayload | TurnoutPayload;
  /** SOS: { sos, duplicate, warning? }; turnout: { turnout } (turnout.warning when no % was computed) */
  result: { sos?: Sos; duplicate?: boolean; warning?: string; turnout?: Turnout };
  sentAt: number;
}

export interface PollingCounts {
  pending: number;
  failed: number;
  total: number;
}

const SCHEMA = `
  CREATE TABLE IF NOT EXISTS polling_outbox (
    id              INTEGER PRIMARY KEY AUTOINCREMENT,
    kind            TEXT NOT NULL,
    assembly_id     TEXT NOT NULL,
    booth_code      TEXT NOT NULL,
    key             TEXT NOT NULL,
    payload         TEXT NOT NULL,
    status          TEXT NOT NULL DEFAULT 'pending',
    attempts        INTEGER NOT NULL DEFAULT 0,
    next_attempt_at INTEGER NOT NULL DEFAULT 0,
    last_error      TEXT,
    rev             INTEGER NOT NULL DEFAULT 1,
    created_at      INTEGER NOT NULL,
    updated_at      INTEGER NOT NULL,
    UNIQUE (kind, assembly_id, booth_code, key)
  );
  CREATE TABLE IF NOT EXISTS polling_sent (
    id          INTEGER PRIMARY KEY AUTOINCREMENT,
    kind        TEXT NOT NULL,
    assembly_id TEXT NOT NULL,
    booth_code  TEXT NOT NULL,
    key         TEXT NOT NULL,
    payload     TEXT NOT NULL,
    result      TEXT NOT NULL,
    sent_at     INTEGER NOT NULL
  );
  CREATE INDEX IF NOT EXISTS polling_sent_lookup ON polling_sent (kind, assembly_id, key);
`;

interface Row {
  id: number;
  kind: string;
  assembly_id: string;
  booth_code: string;
  key: string;
  payload: string;
  status: string;
  attempts: number;
  next_attempt_at: number;
  last_error: string | null;
  rev: number;
  created_at: number;
  updated_at: number;
}

const parse = <T>(s: string | null, fallback: T): T => {
  if (s == null) return fallback;
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
};

function toItem(r: Row): PollingItem {
  const base = {
    id: Number(r.id),
    assemblyId: r.assembly_id,
    boothCode: r.booth_code,
    key: r.key,
    status: r.status as "pending" | "failed",
    attempts: Number(r.attempts),
    nextAttemptAt: Number(r.next_attempt_at),
    lastError: parse<OutboxError | null>(r.last_error, null),
    rev: Number(r.rev),
    createdAt: Number(r.created_at),
    updatedAt: Number(r.updated_at),
  };
  return r.kind === "sos"
    ? { ...base, kind: "sos", payload: parse<SosPayload>(r.payload, {} as SosPayload) }
    : { ...base, kind: "turnout", payload: parse<TurnoutPayload>(r.payload, {} as TurnoutPayload) };
}

/** The sent history keeps what the screens show; a photo (hundreds of KB) is dropped once it is on the server. */
function withoutPhoto(payload: string): string {
  try {
    const p = JSON.parse(payload) as { photo?: unknown };
    if (!p || typeof p !== "object" || !p.photo) return payload;
    const { photo: _drop, ...rest } = p;
    return JSON.stringify({ ...rest, hadPhoto: true });
  } catch {
    return payload;
  }
}

interface SentRow {
  id: number;
  kind: string;
  assembly_id: string;
  booth_code: string;
  key: string;
  payload: string;
  result: string;
  sent_at: number;
}
const toSent = (r: SentRow): SentRecord => ({
  id: Number(r.id),
  kind: r.kind as "sos" | "turnout",
  assemblyId: r.assembly_id,
  boothCode: r.booth_code,
  key: r.key,
  payload: parse(r.payload, {} as SosPayload),
  result: parse(r.result, {}),
  sentAt: Number(r.sent_at),
});

/* ====================================================================== store */

export class PollingStore {
  readonly changes = new Emitter();

  constructor(private readonly db: SqlDb, private readonly now: () => number = Date.now) {}

  async init(): Promise<void> {
    await this.db.exec(SCHEMA);
  }

  private async write<T>(fn: (tx: SqlDb) => Promise<T>): Promise<T> {
    let out!: T;
    await this.db.transaction(async (tx) => {
      out = await fn(tx);
    });
    this.changes.emit("outbox");
    return out;
  }

  /** Idempotent: queueing the same clientId twice keeps the first alert. */
  queueSos(input: { assemblyId: string; boothCode: string; clientId: string; payload: SosPayload }): Promise<number> {
    return this.write(async (tx) => {
      const t = this.now();
      const existing = await tx.first<{ id: number }>("SELECT id FROM polling_outbox WHERE kind = 'sos' AND key = ?", [input.clientId]);
      if (existing) return Number(existing.id);
      const r = await tx.run(
        `INSERT INTO polling_outbox (kind, assembly_id, booth_code, key, payload, created_at, updated_at) VALUES ('sos', ?, ?, ?, ?, ?, ?)`,
        [input.assemblyId, input.boothCode, input.clientId, JSON.stringify(input.payload), t, t]
      );
      return Number(r.lastInsertRowId);
    });
  }

  /** One queued report per booth + date + slot; a newer entry replaces the unsent one. */
  queueTurnout(input: { assemblyId: string; boothCode: string; payload: TurnoutPayload }): Promise<number> {
    const key = `${input.payload.date}|${input.payload.slot}`;
    return this.write(async (tx) => {
      const t = this.now();
      const existing = await tx.first<{ id: number }>(
        "SELECT id FROM polling_outbox WHERE kind = 'turnout' AND assembly_id = ? AND booth_code = ? AND key = ?",
        [input.assemblyId, input.boothCode, key]
      );
      if (existing) {
        await tx.run(
          `UPDATE polling_outbox SET payload = ?, status = 'pending', attempts = 0, next_attempt_at = 0, last_error = NULL,
             rev = rev + 1, updated_at = ? WHERE id = ?`,
          [JSON.stringify(input.payload), t, existing.id]
        );
        return Number(existing.id);
      }
      const r = await tx.run(
        `INSERT INTO polling_outbox (kind, assembly_id, booth_code, key, payload, created_at, updated_at) VALUES ('turnout', ?, ?, ?, ?, ?, ?)`,
        [input.assemblyId, input.boothCode, key, JSON.stringify(input.payload), t, t]
      );
      return Number(r.lastInsertRowId);
    });
  }

  async list(): Promise<PollingItem[]> {
    return (await this.db.all<Row>("SELECT * FROM polling_outbox ORDER BY (kind = 'sos') DESC, id")).map(toItem);
  }

  async get(id: number): Promise<PollingItem | null> {
    const r = await this.db.first<Row>("SELECT * FROM polling_outbox WHERE id = ?", [id]);
    return r ? toItem(r) : null;
  }

  async findByKey(kind: "sos" | "turnout", assemblyId: string, boothCode: string, key: string): Promise<PollingItem | null> {
    const r = await this.db.first<Row>(
      "SELECT * FROM polling_outbox WHERE kind = ? AND assembly_id = ? AND booth_code = ? AND key = ?",
      [kind, assemblyId, boothCode, key]
    );
    return r ? toItem(r) : null;
  }

  /** Pending items that may be sent now: SOS first, then in queue order. */
  async due(dueBy: number): Promise<PollingItem[]> {
    const rows = await this.db.all<Row>(
      "SELECT * FROM polling_outbox WHERE status = 'pending' AND next_attempt_at <= ? ORDER BY (kind = 'sos') DESC, id",
      [dueBy]
    );
    return rows.map(toItem);
  }

  async counts(): Promise<PollingCounts> {
    const rows = await this.db.all<{ status: string; n: number }>("SELECT status, COUNT(*) AS n FROM polling_outbox GROUP BY status");
    const c: PollingCounts = { pending: 0, failed: 0, total: 0 };
    for (const r of rows) {
      if (r.status === "pending" || r.status === "failed") c[r.status] = Number(r.n);
      c.total += Number(r.n);
    }
    return c;
  }

  /** The server accepted it. A turnout edited while in flight stays queued (rev changed). */
  complete(id: number, sentRev: number, result: SentRecord["result"]): Promise<"done" | "requeued"> {
    return this.write(async (tx) => {
      const r = await tx.first<Row>("SELECT * FROM polling_outbox WHERE id = ?", [id]);
      if (!r) return "done";
      await tx.run(
        "INSERT INTO polling_sent (kind, assembly_id, booth_code, key, payload, result, sent_at) VALUES (?, ?, ?, ?, ?, ?, ?)",
        [r.kind, r.assembly_id, r.booth_code, r.key, withoutPhoto(r.payload), JSON.stringify(result), this.now()]
      );
      const del = await tx.run("DELETE FROM polling_outbox WHERE id = ? AND rev = ?", [id, sentRev]);
      if (del.changes === 0) {
        await tx.run("UPDATE polling_outbox SET attempts = 0, next_attempt_at = 0, last_error = NULL, updated_at = ? WHERE id = ?", [this.now(), id]);
        return "requeued";
      }
      return "done";
    });
  }

  markFailed(id: number, sentRev: number, error: OutboxError): Promise<void> {
    return this.write(async (tx) => {
      await tx.run("UPDATE polling_outbox SET status = 'failed', attempts = attempts + 1, last_error = ?, updated_at = ? WHERE id = ? AND rev = ?", [
        JSON.stringify(error),
        this.now(),
        id,
        sentRev,
      ]);
    });
  }

  recordAttempt(id: number, error: OutboxError, nextAttemptAt: number): Promise<void> {
    return this.write(async (tx) => {
      await tx.run(
        "UPDATE polling_outbox SET attempts = attempts + 1, last_error = ?, next_attempt_at = ?, updated_at = ? WHERE id = ? AND status = 'pending'",
        [JSON.stringify(error), nextAttemptAt, this.now(), id]
      );
    });
  }

  retry(id: number): Promise<void> {
    return this.write(async (tx) => {
      await tx.run(
        "UPDATE polling_outbox SET status = 'pending', next_attempt_at = 0, last_error = NULL, rev = rev + 1, updated_at = ? WHERE id = ? AND status = 'failed'",
        [this.now(), id]
      );
    });
  }

  discard(id: number): Promise<void> {
    return this.write(async (tx) => {
      await tx.run("DELETE FROM polling_outbox WHERE id = ?", [id]);
    });
  }

  async sent(assemblyId: string, kind?: "sos" | "turnout", limit = 20): Promise<SentRecord[]> {
    const rows = await this.db.all<SentRow>(
      `SELECT * FROM polling_sent WHERE assembly_id = ? ${kind ? "AND kind = ?" : ""} ORDER BY id DESC LIMIT ?`,
      kind ? [assemblyId, kind, limit] : [assemblyId, limit]
    );
    return rows.map(toSent);
  }

  async findSent(kind: "sos" | "turnout", assemblyId: string, key: string): Promise<SentRecord | null> {
    const r = await this.db.first<SentRow>("SELECT * FROM polling_sent WHERE kind = ? AND assembly_id = ? AND key = ? ORDER BY id DESC LIMIT 1", [
      kind,
      assemblyId,
      key,
    ]);
    return r ? toSent(r) : null;
  }
}

/* ====================================================================== sender */

export interface PollingSyncStatus {
  running: boolean;
  online: boolean | null;
  authRequired: boolean;
  lastRunAt: number | null;
  lastResult: SyncRunResult | null;
  nextRetryAt: number | null;
  counts: PollingCounts;
}

export interface PollingSyncOptions {
  store: PollingStore;
  api: FieldApi;
  now?: () => number;
  random?: () => number;
  timers?: Timers;
  backoff?: { baseMs: number; maxMs: number };
  canSend?: () => boolean;
  /** 401 (expired token or `account_disabled`); the queue is kept */
  onUnauthenticated?: (error: ApiError | null) => void;
}

const EMPTY: SyncRunResult = { sent: 0, conflicts: 0, failed: 0, deferred: 0, stoppedBy: null };

export class PollingSync {
  readonly changes = new Emitter();
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly timers: Timers;
  private readonly backoff: { baseMs: number; maxMs: number };
  private readonly canSend: () => boolean;
  private current: Promise<SyncRunResult> | null = null;
  private rerun = false;
  private rerunForce = false;
  private rerunIgnoreBackoff = false;
  private timer: unknown = null;
  private disposed = false;
  private lastAuthError: ApiError | null = null;
  private readonly unsub: () => void;
  private state: PollingSyncStatus = {
    running: false,
    online: null,
    authRequired: false,
    lastRunAt: null,
    lastResult: null,
    nextRetryAt: null,
    counts: { pending: 0, failed: 0, total: 0 },
  };

  constructor(private readonly opts: PollingSyncOptions) {
    this.now = opts.now ?? Date.now;
    this.random = opts.random ?? Math.random;
    this.timers = opts.timers ?? { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };
    this.backoff = opts.backoff ?? { baseMs: 5_000, maxMs: 10 * 60_000 };
    this.canSend = opts.canSend ?? (() => true);
    this.unsub = opts.store.changes.subscribe(() => void this.refreshCounts());
  }

  get status(): PollingSyncStatus {
    return this.state;
  }

  private patch(p: Partial<PollingSyncStatus>) {
    this.state = { ...this.state, ...p };
    this.changes.emit();
  }

  async refreshCounts() {
    const counts = await this.opts.store.counts().catch(() => this.state.counts);
    this.patch({ counts });
    return counts;
  }

  setOnline(online: boolean): Promise<SyncRunResult> | null {
    const was = this.state.online;
    if (was === online) return null;
    this.patch({ online });
    return online && was !== true ? this.trigger("reconnect") : null;
  }

  resumeAfterLogin(): Promise<SyncRunResult> {
    this.patch({ authRequired: false });
    return this.trigger("login");
  }

  /**
   * Same trigger rules as the mobile engine: "timer" and "save" respect backoff; "manual" also
   * ignores an offline report (an SOS is always tried right away; a real outage fails fast).
   */
  trigger(reason: SyncTrigger): Promise<SyncRunResult> {
    const force = reason === "manual";
    const ignoreBackoff = reason !== "timer" && reason !== "save";
    if (this.current) {
      this.rerun = true;
      this.rerunForce ||= force;
      this.rerunIgnoreBackoff ||= ignoreBackoff;
      return this.current;
    }
    this.current = this.loop(ignoreBackoff, force).finally(() => {
      this.current = null;
    });
    return this.current;
  }

  async idle() {
    while (this.current) await this.current.catch(() => undefined);
  }

  dispose() {
    this.disposed = true;
    this.unsub();
    this.clearTimer();
  }

  private async loop(ignoreBackoff: boolean, force: boolean): Promise<SyncRunResult> {
    this.clearTimer();
    this.patch({ running: true });
    const total: SyncRunResult = { ...EMPTY };
    try {
      for (;;) {
        this.rerun = false;
        this.rerunForce = false;
        this.rerunIgnoreBackoff = false;
        const r = await this.runOnce(ignoreBackoff, force);
        total.sent += r.sent;
        total.failed += r.failed;
        total.deferred += r.deferred;
        total.stoppedBy = r.stoppedBy;
        // A trigger that arrived during this pass (reconnect, a new SOS, "sync now") gets one more
        // pass even if this one stopped on a network error: the network may be back now.
        // (The reused mobile engine stops here instead; its retry timer picks the items up later.)
        if (!this.rerun || this.disposed) break;
        ignoreBackoff = this.rerunIgnoreBackoff;
        force = this.rerunForce;
      }
    } finally {
      const counts = await this.opts.store.counts().catch(() => this.state.counts);
      this.patch({ running: false, lastRunAt: this.now(), lastResult: total, counts });
      await this.armTimer().catch(() => undefined);
    }
    return total;
  }

  private async runOnce(ignoreBackoff: boolean, force: boolean): Promise<SyncRunResult> {
    const result: SyncRunResult = { ...EMPTY };
    if (this.state.online === false && !force) return { ...result, stoppedBy: "offline" };
    if (this.state.authRequired || !this.canSend()) return { ...result, stoppedBy: "unauthenticated" };
    const items = await this.opts.store.due(ignoreBackoff ? Number.MAX_SAFE_INTEGER : this.now());
    for (const snapshot of items) {
      if (this.disposed) break;
      const item = await this.opts.store.get(snapshot.id); // latest payload / rev
      if (!item || item.status !== "pending") continue;
      const outcome = await this.send(item);
      if (outcome === "sent") result.sent++;
      else if (outcome === "failed") result.failed++;
      else if (outcome === "deferred") result.deferred++;
      else if (outcome === "network") return { ...result, stoppedBy: "network" };
      else if (outcome === "unauthenticated") {
        this.patch({ authRequired: true });
        try {
          this.opts.onUnauthenticated?.(this.lastAuthError);
        } catch {
          /* ignore */
        }
        return { ...result, stoppedBy: "unauthenticated" };
      }
    }
    return result;
  }

  private async send(item: PollingItem): Promise<"sent" | "failed" | "deferred" | "network" | "unauthenticated"> {
    try {
      if (item.kind === "sos") {
        const p = item.payload;
        const body: Parameters<FieldApi["raiseSos"]>[1] = { boothCode: item.boothCode, category: p.category, clientId: item.key };
        if (p.note) body.note = p.note;
        if (p.lat != null && p.lng != null) {
          body.lat = p.lat;
          body.lng = p.lng;
          if (p.accuracyM != null) body.accuracyM = p.accuracyM;
        }
        if (p.photo?.data) body.photo = p.photo;
        const r = await this.opts.api.raiseSos(item.assemblyId, body);
        await this.opts.store.complete(item.id, item.rev, { sos: r.sos, duplicate: r.duplicate, ...(r.warning ? { warning: r.warning } : {}) });
      } else {
        const p = item.payload;
        const body: Parameters<FieldApi["reportTurnout"]>[2] = { slot: p.slot, date: p.date, votesPolled: p.votesPolled };
        if (p.ourVotes != null) body.ourVotes = p.ourVotes;
        const r = await this.opts.api.reportTurnout(item.assemblyId, item.boothCode, body);
        await this.opts.store.complete(item.id, item.rev, { turnout: r.turnout });
      }
      return "sent";
    } catch (e) {
      const retryAt = () => this.now() + backoffDelay(item.attempts + 1, this.backoff.baseMs, this.backoff.maxMs, this.random);
      if (e instanceof NetworkError) {
        await this.opts.store.recordAttempt(item.id, { code: "network", message: e.message }, retryAt());
        return "network";
      }
      if (e instanceof ApiError) {
        if (e.status === 401) {
          this.lastAuthError = e;
          return "unauthenticated";
        }
        if (e.status === 408 || e.status === 429 || e.status >= 500) {
          await this.opts.store.recordAttempt(item.id, { code: e.code, status: e.status, message: e.message }, retryAt());
          return "deferred";
        }
        const err: OutboxError = { code: e.code, status: e.status, message: e.message };
        if (e.messageHi) err.messageHi = e.messageHi;
        if (e.details.length) err.details = e.details;
        await this.opts.store.markFailed(item.id, item.rev, err);
        return "failed";
      }
      await this.opts.store
        .recordAttempt(item.id, { code: "error", message: e instanceof Error ? e.message : String(e) }, retryAt())
        .catch(() => undefined);
      return "deferred";
    }
  }

  private clearTimer() {
    if (this.timer != null) this.timers.clear(this.timer);
    this.timer = null;
    if (this.state.nextRetryAt != null) this.patch({ nextRetryAt: null });
  }

  private async armTimer() {
    this.clearTimer();
    if (this.disposed || this.state.online === false || this.state.authRequired || !this.canSend()) return;
    const waits = (await this.opts.store.list()).filter((i) => i.status === "pending").map((i) => i.nextAttemptAt);
    if (!waits.length) return;
    const at = Math.max(Math.min(...waits), this.now() + 1_000);
    this.timer = this.timers.set(() => {
      this.timer = null;
      void this.trigger("timer");
    }, at - this.now());
    this.patch({ nextRetryAt: at });
  }
}
